import React, { useEffect, useRef } from 'react';
import { ArrowRight, Star, Calculator, Phone, Award, TrendingUp, Users2 } from 'lucide-react';
import CountUp from 'react-countup';
import { motion, useInView } from 'framer-motion';

const stats = [
  { number: 14, label: 'Years of Excellence', icon: Award, color: 'text-blue-400' },
  { number: 2000, label: 'Projects Completed', icon: TrendingUp, color: 'text-green-400' },
  { number: 98, label: 'Client Satisfaction', icon: Users2, color: 'text-purple-400' }
];

const testimonials = [
  {
    name: 'Michael Thompson',
    role: 'Homeowner',
    location: 'Scarsdale, NY',
    text: "Golden Touch brought the same level of care and attention to our bathroom update as they would to a major renovation. The results exceeded our expectations."
  },
  {
    name: 'Sarah Chen',
    role: 'Property Developer',
    location: 'Greenwich, CT',
    text: "Whether it's a small repair or a complete renovation, Golden Touch consistently delivers quality work with professional service."
  },
  {
    name: 'James Wilson',
    role: 'Architect',
    location: 'Rye, NY',
    text: "Their ability to handle projects of any scale while maintaining high standards makes them our go-to contractor for all types of work."
  }
];

const milestones = [
  {
    year: '2010',
    title: 'Experience Started',
    description: 'Began providing unparalleled construction services in Westchester.',
    color: 'bg-blue-500'
  },
  {
    year: '2015',
    title: 'Service Expansion',
    description: 'Expanded our service area to cover all of Lower Connecticut.',
    color: 'bg-green-500'
  },
  {
    year: '2024',
    title: 'Innovation Leader',
    description: 'Pioneering smart home integration and sustainable luxury construction.',
    color: 'bg-yellow-500'
  }
];

export function About() {
  const statsRef = useRef(null);
  const isInView = useInView(statsRef, { once: true, margin: "-100px" });

  const openAIEstimator = () => {
    const aiButton = document.querySelector('.fixed.bottom-4') as HTMLButtonElement;
    if (aiButton) {
      aiButton.click();
    }
  };

  return (
    <section id="about" className="py-24 md:py-32 bg-zinc-800/30 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          className="absolute top-20 right-20 w-32 h-32 border border-yellow-400 rounded-full"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-20 left-20 w-24 h-24 border border-yellow-400 rounded-full"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-16 items-center mb-32"
        >
          <div>
            <h2 className="section-subheading">About Us</h2>
            <h3 className="section-heading mb-8 relative">
              Crafting Excellence Since 2010
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "150px" }}
                transition={{ duration: 1, delay: 0.5 }}
                viewport={{ once: true }}
                className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-yellow-400 to-transparent"
              />
            </h3>
            <p className="text-zinc-400 leading-relaxed mb-8">
              Since 2010, Golden Touch has been serving homeowners with projects of all sizes. We believe that every project, 
              whether a simple update or complete renovation, deserves the same level of craftsmanship and attention to detail.
            </p>
            <motion.button 
              whileHover={{ x: 10 }}
              className="text-yellow-400 text-sm tracking-wider flex items-center gap-2 group"
            >
              Learn More <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </motion.button>
          </div>
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative h-[600px] group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <img 
              src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&q=80" 
              alt="Quality Craftsmanship"
              className="absolute inset-0 w-full h-full object-cover rounded-xl group-hover:scale-105 transition-transform duration-700"
            />
          </motion.div>
        </motion.div>

        <div ref={statsRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
          {stats.map((stat, index) => (
            <motion.div 
              key={index}
              initial={{ y: 20, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              whileHover={{ scale: 1.05 }}
              className="text-center bg-zinc-800/50 p-8 rounded-xl border border-zinc-700/50 hover:border-yellow-400/30 transition-all duration-300 group"
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-zinc-700/50 rounded-full group-hover:bg-yellow-400/10 transition-colors duration-300">
                  <stat.icon className={`h-8 w-8 ${stat.color} group-hover:scale-110 transition-transform duration-300`} />
                </div>
              </div>
              <div className="text-4xl md:text-5xl font-light text-yellow-400 mb-4">
                {isInView && (
                  <CountUp
                    end={stat.number}
                    duration={2.5}
                    suffix={stat.label === 'Client Satisfaction' ? '%' : '+'}
                    enableScrollSpy
                    scrollSpyOnce
                  />
                )}
              </div>
              <p className="text-zinc-400 text-sm tracking-wider">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="section-subheading text-center">Our Journey</h2>
          <h3 className="section-heading text-center mb-16 relative">
            Building Trust Through Excellence
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "200px" }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"
            />
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-24">
          {milestones.map((milestone, index) => (
            <motion.div 
              key={index}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-zinc-800/30 p-8 rounded-xl hover:bg-zinc-700/30 transition-all duration-300 border border-zinc-700/50 hover:border-yellow-400/30 group"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3 h-3 ${milestone.color} rounded-full group-hover:scale-125 transition-transform duration-300`} />
                <div className="text-yellow-400 text-sm tracking-wider">{milestone.year}</div>
              </div>
              <h4 className="text-xl text-zinc-100 mb-4">{milestone.title}</h4>
              <p className="text-zinc-400">{milestone.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="section-subheading text-center">Testimonials</h2>
          <h3 className="section-heading text-center mb-16 relative">
            Client Experiences
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "150px" }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"
            />
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-24">
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={index}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-zinc-800/30 p-8 relative rounded-xl hover:bg-zinc-700/30 transition-all duration-300 border border-zinc-700/50 hover:border-yellow-400/30 group"
            >
              {/* Quote decoration */}
              <div className="absolute top-4 right-4 text-6xl text-yellow-400/20 font-serif">"</div>
              
              <div className="flex items-center gap-4 mb-6">
                <div>
                  <h4 className="text-zinc-100 font-medium">{testimonial.name}</h4>
                  <p className="text-zinc-400 text-sm">{testimonial.role} • {testimonial.location}</p>
                </div>
              </div>
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  </motion.div>
                ))}
              </div>
              <p className="text-zinc-400 leading-relaxed">{testimonial.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-zinc-800/50 p-8 sm:p-12 rounded-xl text-center relative overflow-hidden group"
        >
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 via-transparent to-yellow-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <Calculator className="h-12 w-12 text-yellow-400 mx-auto mb-6" />
          <h3 className="text-2xl sm:text-3xl font-light text-zinc-100 mb-4">
            Get an Instant Project Estimate
          </h3>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            Use our AI-powered estimation tool to get a preliminary project cost estimate. For the most accurate pricing, please call us directly.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <a 
              href="tel:9149606433"
              className="flex items-center gap-2 text-yellow-400 text-lg sm:text-xl hover:text-yellow-300 transition-colors"
            >
              <Phone className="h-5 w-5" />
              (914) 960-6433
            </a>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={openAIEstimator}
              className="px-8 py-4 bg-yellow-400 text-zinc-900 flex items-center gap-2 hover:bg-yellow-500 transition-all duration-300 rounded-xl relative overflow-hidden group/btn"
            >
              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
              <Calculator className="h-5 w-5" />
              <span className="text-sm tracking-wider">Try AI Estimator</span>
            </motion.button>
          </div>
          <p className="text-zinc-500 text-sm mt-6">
            * Call (914) 960-6433 for the most accurate estimate
          </p>
        </motion.div>
      </div>
    </section>
  );
}